import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

// Define marketplace theme types
export interface MarketplaceTheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  headerBackground: string;
  footerBackground: string;
}

// Define marketplace configuration
export interface MarketplaceConfig {
  name: string;
  domain: string;
  logo: string;
  theme: MarketplaceTheme;
}

// Available marketplaces and their configurations
const marketplaceConfigs: Record<string, MarketplaceConfig> = {
  soc: {
    name: "SOC Marketplace",
    domain: "soc.example.com",
    logo: "",
    theme: {
      primary: "#2563eb", // Blue
      secondary: "#1e40af",
      accent: "#f97316", // Orange accent
      background: "#f8fafc",
      text: "#334155",
      headerBackground: "#ffffff",
      footerBackground: "#1e3a8a",
    },
  },
  dev: {
    name: "DevMarket (DevSecOps)",
    domain: "dev.example.com",
    logo: "",
    theme: {
      primary: "#16a34a", // Green
      secondary: "#166534",
      accent: "#9333ea", // Purple accent
      background: "#f8fafc",
      text: "#334155",
      headerBackground: "#ffffff",
      footerBackground: "#064e3b",
    },
  },
  dm: {
    name: "Digital Marketing",
    domain: "dm.example.com",
    logo: "",
    theme: {
      primary: "#dc2626", // Red
      secondary: "#b91c1c",
      accent: "#3b82f6", // Blue accent
      background: "#f8fafc",
      text: "#334155",
      headerBackground: "#ffffff",
      footerBackground: "#7f1d1d",
    },
  },
  sw: {
    name: "Dev Stack Marketplace",
    domain: "sw.example.com",
    logo: "",
    theme: {
      primary: "#9333ea", // Purple
      secondary: "#7e22ce",
      accent: "#f59e0b", // Amber accent
      background: "#f8fafc", // Light gray background
      text: "#334155", // Slate text
      headerBackground: "#ffffff",
      footerBackground: "#7f1d1d",
    },
  },
  // Default for development environments - using Dev Stack as default
  localhost: {
    name: "Dev Stack Marketplace",
    domain: "localhost",
    logo: "", // You might want to add a specific logo for Dev Stack later
    theme: {
      primary: "#2563eb", // Blue
      secondary: "#1e40af",
      accent: "#f97316", // Orange accent
      background: "#f8fafc", // Light gray background
      text: "#334155", // Slate text
      headerBackground: "#ffffff",
      footerBackground: "#1e3a8a",
    },
  },
};

const defaultMarketplace: MarketplaceConfig = {
  name: "Dev Stack Marketplace",
  domain: "localhost",
  logo: "",
  theme: {
    primary: "#2563eb", // Blue
    secondary: "#1e40af",
    accent: "#f97316", // Orange accent
    background: "#f8fafc", // Light gray background
    text: "#334155", // Slate text
    headerBackground: "#ffffff",
    footerBackground: "#1e3a8a",
  },
};

interface MarketplaceContextType {
  currentMarketplace: MarketplaceConfig;
  theme: MarketplaceTheme;
}

const MarketplaceContext = createContext<MarketplaceContextType | undefined>(
  undefined
);

export const useMarketplace = () => {
  const context = useContext(MarketplaceContext);
  if (context === undefined) {
    throw new Error("useMarketplace must be used within a MarketplaceProvider");
  }
  return context;
};

interface MarketplaceProviderProps {
  children: ReactNode;
}

export const MarketplaceProvider: React.FC<MarketplaceProviderProps> = ({
  children,
}) => {
  const [currentMarketplace, setCurrentMarketplace] =
    useState<MarketplaceConfig>(marketplaceConfigs.localhost);

  // First effect: Set the current marketplace based on hostname (only runs once)
  useEffect(() => {
    // Identify the current marketplace based on hostname
    const hostname = window.location.hostname;

    // Check for exact domain matches first
    if (marketplaceConfigs[hostname]) {
      setCurrentMarketplace(marketplaceConfigs[hostname]);
    }
    // Then check for subdomain matches
    else {
      // Check for pattern matching (e.g., soc.example.com, dev.example.com)
      const subdomain = hostname.split(".")[0];
      if (marketplaceConfigs[subdomain]) {
        setCurrentMarketplace(marketplaceConfigs[subdomain]);
      } else {
        // Default to localhost/SOC if no match
        setCurrentMarketplace(marketplaceConfigs.localhost);
      }
    }
  }, []); // Empty dependency array means this runs once on component mount

  // Second effect: Apply theme when currentMarketplace changes
  useEffect(() => {
    // Apply CSS variables to the document root for theming
    const root = document.documentElement;
    const theme = currentMarketplace.theme;

    root.style.setProperty("--color-primary", theme.primary);
    root.style.setProperty("--color-secondary", theme.secondary);
    root.style.setProperty("--color-accent", theme.accent);
    root.style.setProperty("--color-background", theme.background);
    root.style.setProperty("--color-text", theme.text);
    root.style.setProperty("--color-header-bg", theme.headerBackground);
    root.style.setProperty("--color-footer-bg", theme.footerBackground);
  }, [currentMarketplace]); // This will only run when currentMarketplace changes

  return (
    <MarketplaceContext.Provider
      value={{
        currentMarketplace,
        theme: currentMarketplace.theme,
      }}
    >
      {children}
    </MarketplaceContext.Provider>
  );
};

export default MarketplaceContext;
